import type {RouteLocationNormalized} from 'vue-router';
import type {ISbStoryData} from 'storyblok-js-client';

export default defineNuxtRouteMiddleware(async function (route: RouteLocationNormalized) {
  const locale = getLocale();

  const page = useState<ISbStoryData | undefined>('storyblok.page');
  const pages = useState<Record<string, ISbStoryData>>('storyblok.pages', () => ({}));

  if (import.meta.server) {
    pages.value = {};
  }

  const slug = route.path.replace(/^\//, '');
  const key = `${locale}#${slug}`;

  const {isPreview} = useStoryblokPreview();

  page.value = (pages.value[key] ||= await fetchPage(slug, locale, isPreview.value));

  populateMeta(page, locale);
});

function getLocale(): string | undefined {
  const nuxt = useNuxtApp();

  if (nuxt.$i18n) {
    return (nuxt.$i18n as {
      locale: Ref<string>
    }).locale.value;
  }
}

function populateMeta(page: Ref<ISbStoryData | undefined>, locale: string | undefined) {
  const {seo} = useStoryblokStory(page);

  if (!seo.value) {
    console.error('No `bttr-seo` field found on page, did you add the `bttr-seo` field to the story?');
  }

  const pageTitle = page.value?.translated_slugs?.find(translation => translation.lang === locale)?.name ?? page.value?.name;

  useServerSeoMeta(Object.fromEntries(
    Object.entries({
      robots: page.value?.published_at && (!seo.value || seo.value?.index) ? 'follow index' : 'nofollow noindex',
      description: seo.value?.description,
      ogDescription: seo.value?.services.og.description || seo.value?.description,
      ogTitle: seo.value?.services.og.title || seo.value?.title || pageTitle,
      ogImage: seo.value?.services.og.image?.filename || seo.value?.image?.filename,
      twitterImage: seo.value?.services.x.image?.filename || seo.value?.image?.filename,
      twitterTitle: seo.value?.services.x.title || seo.value?.title || pageTitle,
      twitterDescription: seo.value?.services.x.description || seo.value?.description,
    }).filter(([, value]) => value !== undefined),
  ));

  useSeoMeta({
    title: seo.value?.title || pageTitle,
  });
}

async function fetchPage(slug: string, locale: string | undefined, isPreview: boolean) {
  // Strip locale prefix from slug
  if (locale && (slug === locale || slug.startsWith(locale + '/'))) {
    slug = slug.slice(locale.length + 1);
  }

  if (isPreview) {
    slug = slug.slice('_preview'.length + 1);
  }

  const config = useStoryblokConfig();
  const story = await useAsyncStoryblokStory(slug || 'home', locale);

  const {isPublished} = useStoryblokStory(story);

  if (!story?.content || story.slug === config.settings?.storyName || (!isPublished.value && !isPreview)) {
    throw createError({
      statusCode: 404,
    });
  }

  return story;
}

async function useAsyncStoryblokStory(slug: string, locale: string | undefined) {
  const api = useStoryblokApi();
  const config = useStoryblokConfig();

  const {data} = await api.getStory(slug, {
    version: useStoryblokVersion(),
    resolve_relations: config.pages?.resolveRelations ?? [],
    language: locale,
    cv: useStoryblokContentVersion().value,
  });

  return data?.story;
}
